import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Snackbar,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
  Grid,
  InputAdornment,
  Switch,
  FormControlLabel,
  Chip,
} from "@mui/material";
import {
  Refresh as RefreshIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  LocationOn as LocationIcon,
  Save as SaveIcon,
  Close as CloseIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Add as AddIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
} from "@mui/icons-material";
import axios from "axios";
import { getAuthHeaders } from "../../utils/auth";

const LocationManagement = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [locations, setLocations] = useState([]);
  const [editingLocation, setEditingLocation] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editName, setEditName] = useState("");
  const [editAddress, setEditAddress] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [editHourlyCost, setEditHourlyCost] = useState("");
  const [editShowInTimetable, setEditShowInTimetable] = useState(true);
  const [editTimetableName, setEditTimetableName] = useState("");
  const [editIsMainLocation, setEditIsMainLocation] = useState(false);
  const [editGoogleMapsEmbed, setEditGoogleMapsEmbed] = useState("");
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [newTimetableName, setNewTimetableName] = useState("");
  const [newAddress, setNewAddress] = useState("");
  const [newDescription, setNewDescription] = useState("");
  const [newHourlyCost, setNewHourlyCost] = useState("");
  const [newShowInTimetable, setNewShowInTimetable] = useState(true);
  const [newIsMainLocation, setNewIsMainLocation] = useState(false);
  const [newGoogleMapsEmbed, setNewGoogleMapsEmbed] = useState("");
  const [addLoading, setAddLoading] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingLocation, setDeletingLocation] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const fetchLocations = async () => {
    setLoading(true);
    setError("");
    try {
      const headers = await getAuthHeaders();
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_ADDRESS}/costs/locations`,
        { headers }
      );
      if (response.status === 200) {
        setLocations(response.data);
      }
    } catch (err) {
      console.error("Failed to fetch locations", err);
      setError("Nie udało się załadować listy lokalizacji");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLocations();
  }, []);

  const handleEditClick = (location) => {
    setEditingLocation(location);
    setEditName(location.name || "");
    setEditAddress(location.address || "");
    setEditDescription(location.description || "");
    setEditHourlyCost(location.hourly_cost?.toString() || "0");
    setEditShowInTimetable(location.show_in_timetable ?? true);
    setEditTimetableName(location.timetable_name || "");
    setEditIsMainLocation(location.is_main_location ?? false);
    setEditGoogleMapsEmbed(location.google_maps_embed || "");
    setEditDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!editingLocation) return;
    
    try {
      const headers = await getAuthHeaders();
      const response = await axios.put(
        `${process.env.REACT_APP_BACKEND_ADDRESS}/costs/locations/${editingLocation.id}`,
        {
          name: editName.trim(),
          address: editAddress.trim(),
          description: editDescription.trim() || "",
          hourly_cost: parseFloat(editHourlyCost) || 0,
          show_in_timetable: editShowInTimetable,
          timetable_name: editTimetableName || "",
          is_main_location: editIsMainLocation,
          google_maps_embed: editGoogleMapsEmbed || "",
        },
        { headers }
      );
      
      if (response.status === 200) {
        setLocations(prev => 
          prev.map(l => l.id === editingLocation.id ? response.data : l)
        );
        setSnackbar({ open: true, message: "Zaktualizowano lokalizację", severity: "success" });
        setEditDialogOpen(false);
        setEditingLocation(null);
      }
    } catch (err) {
      console.error("Failed to update location", err);
      setSnackbar({ open: true, message: "Nie udało się zaktualizować lokalizacji", severity: "error" });
    }
  };

  const handleToggleTimetable = async (location) => {
    try {
      const headers = await getAuthHeaders();
      const response = await axios.put(
        `${process.env.REACT_APP_BACKEND_ADDRESS}/costs/locations/${location.id}`,
        { show_in_timetable: !location.show_in_timetable },
        { headers }
      );
      
      if (response.status === 200) {
        setLocations(prev => 
          prev.map(l => l.id === location.id ? response.data : l)
        );
        setSnackbar({
          open: true,
          message: response.data.show_in_timetable
            ? `"${location.name}" jest teraz widoczna w grafiku`
            : `"${location.name}" została ukryta z grafiku`,
          severity: "success",
        });
      }
    } catch (err) {
      console.error("Failed to toggle timetable visibility", err);
      setSnackbar({ open: true, message: "Nie udało się zmienić widoczności", severity: "error" });
    }
  };

  const handleToggleMainLocation = async (location) => {
    try {
      const headers = await getAuthHeaders();
      const response = await axios.put(
        `${process.env.REACT_APP_BACKEND_ADDRESS}/costs/locations/${location.id}`,
        { is_main_location: !location.is_main_location },
        { headers }
      );
      
      if (response.status === 200) {
        setLocations(prev => 
          prev.map(l => l.id === location.id ? response.data : l)
        );
        setSnackbar({
          open: true,
          message: response.data.is_main_location
            ? `"${location.name}" została oznaczona jako główna lokalizacja`
            : `"${location.name}" nie jest już główną lokalizacją`,
          severity: "success",
        });
      }
    } catch (err) {
      console.error("Failed to toggle main location", err);
      setSnackbar({ open: true, message: "Nie udało się zmienić statusu głównej lokalizacji", severity: "error" });
    }
  };

  const handleOpenAddDialog = () => {
    setNewName("");
    setNewTimetableName("");
    setNewAddress("");
    setNewDescription("");
    setNewHourlyCost("");
    setNewShowInTimetable(true);
    setNewIsMainLocation(false);
    setNewGoogleMapsEmbed("");
    setAddDialogOpen(true);
  };

  const handleCreateLocation = async () => {
    if (!newName.trim() || !newAddress.trim()) {
      setSnackbar({ open: true, message: "Nazwa i adres są wymagane", severity: "warning" });
      return;
    }

    setAddLoading(true);
    try {
      const headers = await getAuthHeaders();
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_ADDRESS}/lesson-type-locations/`,
        {
          name: newName.trim(),
          timetable_name: newTimetableName.trim() || null,
          address: newAddress.trim(),
          description: newDescription.trim() || null,
          show_in_timetable: newShowInTimetable,
          is_main_location: newIsMainLocation,
          google_maps_embed: newGoogleMapsEmbed.trim() || null,
        },
        { headers }
      );

      if (response.status === 200 || response.status === 201) {
        setSnackbar({ open: true, message: `Dodano lokalizację "${newName}"`, severity: "success" });
        setAddDialogOpen(false);
        fetchLocations();
      }
    } catch (err) {
      console.error("Failed to create location", err);
      const detail = err.response?.data?.detail || "Nie udało się dodać lokalizacji";
      setSnackbar({ open: true, message: detail, severity: "error" });
    } finally {
      setAddLoading(false);
    }
  };

  const handleDeleteClick = (location) => {
    setDeletingLocation(location);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!deletingLocation) return;

    setDeleteLoading(true);
    try {
      const headers = await getAuthHeaders();
      const response = await axios.delete(
        `${process.env.REACT_APP_BACKEND_ADDRESS}/lesson-type-locations/${deletingLocation.id}`,
        { headers }
      );

      if (response.status === 200) {
        setLocations(prev => prev.filter(l => l.id !== deletingLocation.id));
        setSnackbar({ open: true, message: `Usunięto lokalizację "${deletingLocation.name}"`, severity: "success" });
      }
    } catch (err) {
      console.error("Failed to delete location", err);
      const detail = err.response?.data?.detail || "Nie udało się usunąć lokalizacji";
      setSnackbar({ open: true, message: detail, severity: "error" });
    } finally {
      setDeleteLoading(false);
      setDeleteDialogOpen(false);
      setDeletingLocation(null);
    }
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return "-";
    return new Intl.NumberFormat('pl-PL', {
      style: 'currency',
      currency: 'PLN'
    }).format(amount);
  };

  return (
    <Box sx={{ width: "100%", p: { xs: 1, sm: 2, md: 3 } }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 2 }}>
        <Box>
          <Typography variant="h5" component="h2" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
            <LocationIcon color="primary" />
            Zarządzanie Lokalizacjami
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Ustawianie kosztów godzinowych wynajmu, widoczności w grafiku i głównych lokalizacji
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenAddDialog}
            color="primary"
          >
            Dodaj lokalizację
          </Button>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchLocations}
            disabled={loading}
          >
            Odśwież
          </Button>
        </Box>
      </Box>

      {/* Summary Card */}
      <Card sx={{ mb: 3, background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)' }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={6} sm={3}>
              <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
                Liczba lokalizacji
              </Typography>
              <Typography variant="h3" sx={{ color: 'white', fontWeight: 700 }}>
                {locations.length}
              </Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
                Widoczne w grafiku
              </Typography>
              <Typography variant="h3" sx={{ color: 'white', fontWeight: 700 }}>
                {locations.filter(l => l.show_in_timetable).length}
              </Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
                Główne lokalizacje
              </Typography>
              <Typography variant="h3" sx={{ color: 'white', fontWeight: 700 }}>
                {locations.filter(l => l.is_main_location).length}
              </Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
                Z kosztem godzinowym
              </Typography>
              <Typography variant="h3" sx={{ color: 'white', fontWeight: 700 }}>
                {locations.filter(l => l.hourly_cost > 0).length}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {!loading && !error && (
        <Paper sx={{ overflow: 'hidden' }}>
          <TableContainer>
            <Table size={isMobile ? "small" : "medium"}>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell sx={{ fontWeight: 600 }}>Nazwa lokalizacji</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600 }}>Główna</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600 }}>Widoczna w grafiku</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Koszt godzinowy</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600 }}>Akcje</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {locations.map((location) => (
                  <TableRow key={location.id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {location.name}
                        </Typography>
                        {location.is_main_location && (
                          <Chip
                            size="small"
                            icon={<StarIcon fontSize="small" />}
                            label="Główna"
                            color="warning"
                            variant="filled"
                          />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Switch
                        checked={location.is_main_location ?? false}
                        onChange={() => handleToggleMainLocation(location)}
                        color="warning"
                        size="small"
                        inputProps={{ 'aria-label': 'Główna lokalizacja' }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Switch
                        checked={location.show_in_timetable ?? true}
                        onChange={() => handleToggleTimetable(location)}
                        color="primary"
                        size="small"
                        inputProps={{ 'aria-label': 'Widoczność w grafiku' }}
                      />
                      <Chip
                        size="small"
                        icon={location.show_in_timetable ? <VisibilityIcon fontSize="small" /> : <VisibilityOffIcon fontSize="small" />}
                        label={location.show_in_timetable ? "Widoczna" : "Ukryta"}
                        color={location.show_in_timetable ? "success" : "default"}
                        variant={location.show_in_timetable ? "filled" : "outlined"}
                        sx={{ ml: 1 }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          fontWeight: 600,
                          color: location.hourly_cost > 0 ? 'success.main' : 'text.secondary'
                        }}
                      >
                        {formatCurrency(location.hourly_cost)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleEditClick(location)}
                        title="Edytuj lokalizację"
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDeleteClick(location)}
                        title="Usuń lokalizację"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {locations.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                        Brak lokalizacji w systemie
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Add Dialog */}
      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AddIcon color="primary" />
            Dodaj nową lokalizację
          </Box>
          <IconButton size="small" onClick={() => setAddDialogOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <TextField
              label="Nazwa lokalizacji"
              fullWidth
              required
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="np. Sala Główna"
              autoFocus
            />
            <TextField
              label="Nazwa w grafiku"
              fullWidth
              value={newTimetableName}
              onChange={(e) => setNewTimetableName(e.target.value)}
              placeholder="np. Sala Główna"
              helperText="Opcjonalnie — nazwa wyświetlana w grafiku (jeśli pusta, używana będzie główna nazwa)"
            />
            <TextField
              label="Adres"
              fullWidth
              required
              value={newAddress}
              onChange={(e) => setNewAddress(e.target.value)}
              placeholder="np. ul. Przykładowa 10, Warszawa"
            />
            <TextField
              label="Opis"
              fullWidth
              multiline
              rows={2}
              value={newDescription}
              onChange={(e) => setNewDescription(e.target.value)}
              placeholder="Opcjonalny opis lokalizacji"
            />
            <TextField
              label="Koszt godzinowy"
              type="number"
              fullWidth
              value={newHourlyCost}
              onChange={(e) => setNewHourlyCost(e.target.value)}
              InputProps={{
                endAdornment: <InputAdornment position="end">PLN/godz.</InputAdornment>,
                inputProps: { min: 0, step: 0.01 }
              }}
              helperText="Opcjonalnie — koszt wynajmu za godzinę"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={newShowInTimetable}
                  onChange={(e) => setNewShowInTimetable(e.target.checked)}
                  color="primary"
                />
              }
              label="Widoczna w grafiku"
            />
            <Typography variant="caption" color="text.secondary" sx={{ mt: -2 }}>
              Gdy wyłączona, lokalizacja nie pojawi się jako zakładka w grafiku zajęć
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={newIsMainLocation}
                  onChange={(e) => setNewIsMainLocation(e.target.checked)}
                  color="warning"
                />
              }
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <StarIcon fontSize="small" color={newIsMainLocation ? "warning" : "disabled"} />
                  Główna lokalizacja (studio)
                </Box>
              }
            />
            <Typography variant="caption" color="text.secondary" sx={{ mt: -2 }}>
              Główne lokalizacje wyświetlają się w selektorze studia na stronie głównej. Gdy jest więcej niż jedna, użytkownik może przełączać się między nimi.
            </Typography>
            {newIsMainLocation && (
              <TextField
                label="Google Maps embed URL"
                fullWidth
                value={newGoogleMapsEmbed}
                onChange={(e) => setNewGoogleMapsEmbed(e.target.value)}
                placeholder="https://www.google.com/maps/embed?pb=..."
                helperText="Link embed do Google Maps (skopiuj z Google Maps → Udostępnij → Umieść mapę)"
                multiline
                rows={2}
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setAddDialogOpen(false)} startIcon={<CloseIcon />} disabled={addLoading}>
            Anuluj
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateLocation}
            startIcon={addLoading ? <CircularProgress size={18} /> : <SaveIcon />}
            disabled={addLoading || !newName.trim() || !newAddress.trim()}
          >
            Dodaj
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LocationIcon color="primary" />
            Edytuj lokalizację
          </Box>
          <IconButton size="small" onClick={() => setEditDialogOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {editingLocation && (
            <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              <TextField
                label="Nazwa lokalizacji"
                fullWidth
                required
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                placeholder="np. Sala Główna"
                autoFocus
              />
              <TextField
                label="Nazwa w grafiku"
                fullWidth
                value={editTimetableName}
                onChange={(e) => setEditTimetableName(e.target.value)}
                placeholder="np. Sala Główna"
                helperText="Nazwa wyświetlana w grafiku zajęć (jeśli pusta, używana będzie główna nazwa)"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={editShowInTimetable}
                    onChange={(e) => setEditShowInTimetable(e.target.checked)}
                    color="primary"
                  />
                }
                label="Widoczna w grafiku"
              />
              <Typography variant="caption" color="text.secondary" sx={{ mt: -2 }}>
                Gdy wyłączona, lokalizacja nie będzie widoczna jako zakładka w grafiku zajęć
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={editIsMainLocation}
                    onChange={(e) => setEditIsMainLocation(e.target.checked)}
                    color="warning"
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <StarIcon fontSize="small" color={editIsMainLocation ? "warning" : "disabled"} />
                    Główna lokalizacja (studio)
                  </Box>
                }
              />
              <Typography variant="caption" color="text.secondary" sx={{ mt: -2 }}>
                Główne lokalizacje wyświetlają się w selektorze studia na stronie głównej
              </Typography>
              {editIsMainLocation && (
                <TextField
                  label="Google Maps embed URL"
                  fullWidth
                  value={editGoogleMapsEmbed}
                  onChange={(e) => setEditGoogleMapsEmbed(e.target.value)}
                  placeholder="https://www.google.com/maps/embed?pb=..."
                  helperText="Link embed do Google Maps (skopiuj z Google Maps → Udostępnij → Umieść mapę)"
                  multiline
                  rows={2}
                />
              )}
              <TextField
                label="Adres"
                fullWidth
                required
                value={editAddress}
                onChange={(e) => setEditAddress(e.target.value)}
                placeholder="np. ul. Przykładowa 10, Warszawa"
              />
              <TextField
                label="Opis"
                fullWidth
                multiline
                rows={2}
                value={editDescription}
                onChange={(e) => setEditDescription(e.target.value)}
                placeholder="Opcjonalny opis lokalizacji"
              />
              <TextField
                label="Koszt godzinowy"
                type="number"
                fullWidth
                value={editHourlyCost}
                onChange={(e) => setEditHourlyCost(e.target.value)}
                InputProps={{
                  endAdornment: <InputAdornment position="end">PLN/godz.</InputAdornment>,
                  inputProps: { min: 0, step: 0.01 }
                }}
                helperText="Koszt wynajmu lokalizacji za godzinę zajęć"
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setEditDialogOpen(false)} startIcon={<CloseIcon />}>
            Anuluj
          </Button>
          <Button 
            variant="contained" 
            onClick={handleSaveEdit}
            startIcon={<SaveIcon />}
          >
            Zapisz
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => !deleteLoading && setDeleteDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <DeleteIcon color="error" />
          Usuń lokalizację
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Czy na pewno chcesz usunąć lokalizację <strong>"{deletingLocation?.name}"</strong>?
          </Typography>
          <Typography variant="body2" color="error" sx={{ mt: 1 }}>
            Ta operacja jest nieodwracalna.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleteLoading}>
            Anuluj
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmDelete}
            startIcon={deleteLoading ? <CircularProgress size={18} /> : <DeleteIcon />}
            disabled={deleteLoading}
          >
            Usuń
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default LocationManagement;
